/**
 * Colour theme for one block.
 *
 * Stored on the block as `colorTheme`:
 *   { paletteId }                                  a palette saved on the theme
 *   { custom: { background, text, accent, muted } } colours for this block only
 * plus `bleed: true` to paint the background across the full window width.
 * No colorTheme means the block uses the page colours.
 *
 * Palettes live on the site theme as `palettes: [{ id, name, colors }]`.
 */

import { hexToRgb, parseColor } from '../theme/colors.js';

export const BLOCK_COLOR_KEYS = ['background', 'text', 'accent', 'muted'];
export const DEFAULT_PALETTE_ID = 'site';
export const MIN_CONTRAST = 4.5;

function cleanColors(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const next = {};
    BLOCK_COLOR_KEYS.forEach((key) => {
        const value = parseColor(raw[key]);
        if (value) next[key] = value;
    });
    return Object.keys(next).length ? next : null;
}

/**
 * @param {Object} raw
 * @returns {Object|null}
 */
export function cleanColorTheme(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const bleed = raw.bleed === true ? { bleed: true } : {};
    if (typeof raw.paletteId === 'string' && raw.paletteId) return { paletteId: raw.paletteId, ...bleed };
    const custom = cleanColors(raw.custom);
    return custom ? { custom, ...bleed } : null;
}

/**
 * Saved palettes, always starting with the site's own colours.
 * @param {Object} theme
 * @returns {Array<{ id: string, name: string, colors: Object }>}
 */
export function themePalettes(theme) {
    const site = {
        id: DEFAULT_PALETTE_ID,
        name: 'Site colours',
        colors: cleanColors({ background: theme?.colors?.background, text: theme?.colors?.text }) || {},
    };
    const saved = (Array.isArray(theme?.palettes) ? theme.palettes : [])
        .filter((entry) => entry && typeof entry.id === 'string' && entry.id !== DEFAULT_PALETTE_ID)
        .map((entry) => ({
            id: entry.id,
            name: String(entry.name || 'Palette'),
            colors: cleanColors(entry.colors) || {},
        }));
    return [site, ...saved];
}

/**
 * The colours a block paints with, or null when it follows the page.
 * @param {Object} colorTheme
 * @param {Object} theme
 */
export function blockColors(colorTheme, theme) {
    const clean = cleanColorTheme(colorTheme);
    if (!clean) return null;
    const colors = clean.paletteId
        ? themePalettes(theme).find((entry) => entry.id === clean.paletteId)?.colors
        : clean.custom;
    return colors && Object.keys(colors).length ? colors : null;
}

/**
 * Wrapper style for a themed block: colour variables for everything inside,
 * and the block's own background and text colour.
 * @returns {Object} style properties, empty when the block follows the page
 */
export function colorVars(colorTheme, theme) {
    const colors = blockColors(colorTheme, theme);
    if (!colors) return {};
    const vars = {};
    if (colors.background) vars['--color-background'] = colors.background;
    if (colors.text) vars['--color-text'] = colors.text;
    if (colors.accent) vars['--color-accent'] = colors.accent;
    if (colors.muted) vars['--color-muted'] = colors.muted;
    // Declared on :root from the page colours, so it has to be worked out again here.
    vars['--color-placeholder'] = 'color-mix(in srgb, var(--color-background) 94%, var(--color-text))';
    const style = { ...vars, color: 'var(--color-text)' };
    if (colors.background) {
        style.backgroundColor = 'var(--color-background)';
        if (cleanColorTheme(colorTheme).bleed) {
            // Paints the window width without changing the block's layout box.
            style.boxShadow = '0 0 0 100vmax var(--color-background)';
            style.clipPath = 'inset(0 -100vmax)';
        }
    }
    return style;
}

/**
 * Colours for one column of a columns block. Its text takes them like a
 * block's; the background is painted by the columns CSS, out into the gaps
 * and to the window edge, so it comes back as a flag.
 * @returns {{ style: Object, fill: boolean }}
 */
export function columnColors(colorTheme, theme) {
    const { backgroundColor, boxShadow: _shadow, clipPath: _clip, ...style } = colorVars(colorTheme, theme);
    return { style, fill: Boolean(backgroundColor) };
}

function luminance(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) return null;
    const channel = (value) => {
        const c = value / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/**
 * WCAG contrast ratio between two colours, or null when either is missing.
 */
export function contrastRatio(a, b) {
    const la = luminance(parseColor(a));
    const lb = luminance(parseColor(b));
    if (la == null || lb == null) return null;
    const [hi, lo] = la > lb ? [la, lb] : [lb, la];
    return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
}

/** A new palette id that does not clash with the given palettes. */
export function newPaletteId(palettes) {
    const taken = new Set((palettes || []).map((entry) => entry.id));
    let n = (palettes || []).length;
    let id = `palette-${n}`;
    while (taken.has(id)) {
        n += 1;
        id = `palette-${n}`;
    }
    return id;
}
